import "./tags-input.scss";

import {
    autoinject,
    bindable,
    bindingMode,
    computedFrom,
    containerless,
    customElement,
    observable
} from "aurelia-framework";
import { ValidationRules } from "aurelia-validation";

import nameof from "../../../common/nameof";
import { IValidateCustomElement } from "../../../interfaces/i-validate-custom-element";
import { TypeaheadInput } from "../typeahead-input/typeahead-input";
import { ITypeaheadOptions, TypeaheadComponentData } from "./../../../interfaces/i-typeahead";

@autoinject
@containerless
@customElement("tags-input")
export class TagsInput {
    @bindable({ defaultBindingMode: bindingMode.twoWay })
    public data: ITypeaheadOptions[] | TypeaheadComponentData;
    @bindable({ defaultBindingMode: bindingMode.twoWay })
    public tags: ITypeaheadOptions[] = [];
    @bindable({ defaultBindingMode: bindingMode.twoWay })
    public closeOnSelection: boolean = true;
    @bindable({ defaultBindingMode: bindingMode.oneTime })
    public validation: IValidateCustomElement = {
        required: false
    };
    @observable({
        changeHandler: nameof<TagsInput>("valueChanged")
    })
    public value: ITypeaheadOptions = null;
    @bindable({ defaultBindingMode: bindingMode.twoWay })
    public openOnFocus: boolean = false;
    @bindable({ defaultBindingMode: bindingMode.toView })
    public classes: string = "";
    @bindable({ defaultBindingMode: bindingMode.oneTime })
    public placeholder: string = "Start Typing...";
    @bindable({ defaultBindingMode: bindingMode.toView })
    public disabled: boolean = false;
    @bindable({ defaultBindingMode: bindingMode.oneTime })
    public showTagsByValue: boolean = false;
    @bindable({ defaultBindingMode: bindingMode.toView })
    public showRemoveTag: boolean = true;
    // Use Slots to Control Input, Tags Component switches to Display only mode
    @bindable({ defaultBindingMode: bindingMode.oneTime })
    public displayOnly: boolean = false;
    @bindable({ defaultBindingMode: bindingMode.toView })
    public resultsLimit: number = 30;
    @bindable({ defaultBindingMode: bindingMode.toView })
    public fontWeight: string;
    public typeaheadInputRef: TypeaheadInput;
    public tagsWrapperMarkupRef: HTMLElement;
    public tagsLength: number = 0;

    @computedFrom(
        `${nameof<TagsInput>("tags")}.length`,
        nameof<TagsInput>("data"),
        `${nameof<TagsInput>("data")}.length`
    )
    public get tagsData(): TypeaheadComponentData {
        let tagsValues = this.tags?.length ? this.tags.map((item) => item?.value) : [];
        if (Array.isArray(this.data)) {
            this.tagsLength = this.tags?.length;
            return this.data.filter((item: ITypeaheadOptions) => {
                return !tagsValues.includes(item.value);
            });
        } else {
            return this.data;
        }
    }

    // tslint:disable-next-line: no-empty
    public set tagsData(data: TypeaheadComponentData) {}

    public attached() {
        if (this.validation?.required) {
            this.initValidation();
        }
    }

    public valueChanged() {
        if (this.value) {
            this.addTag(this.value);
            this.typeaheadInputRef.clearFilter();
            this.typeaheadInputRef.resetFilter();
        }
    }

    private addTag(tag: ITypeaheadOptions) {
        if (!this.tags.find((item: ITypeaheadOptions) => item.value == tag.value)) {
            this.tags = this.tags.concat([this.value]);
            this.tagsLength = this.tags.length;
        }
    }

    public removeTag(tag: ITypeaheadOptions) {
        this.tags = this.tags.filter((item) => item.value !== tag.value);
        this.tagsLength = this.tags.length;
    }

    public handleWrapperClick(event: MouseEvent) {
        if ((event.target as Element).className.includes("tags-wrapper") && this.typeaheadInputRef) {
            this.typeaheadInputRef.focusInput();
        }
    }

    private initValidation() {
        ValidationRules.ensure((x: TagsInput) => x.tags)
            .minItems(1)
            .withMessage(`${this.validation.message}`)
            .ensure((x: TagsInput) => x.tagsLength)
            .required()
            .withMessage(`${this.validation.message}`)
            .satisfies((x) => x != 0)
            .withMessage(`${this.validation.message}`)
            .on(this);
    }
}
